/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2016, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../util/DashboardFormatter',
	'../../../widgets/livewidget/nls/StringResources',
	'./CrosstabEdgeIterator',
	'../../../widgets/livewidget/query/QueryResultDataUtils'
], function (Formatter, StringResources, CrosstabEdgeIterator, QueryResultDataUtils) {

	/**
			This class pivots the query data,
				[[column-level1, column-level2, row-level1, row-level2, measure1, measure2, .. measure-n ],
				[column-level1, column-level2, row-level1, row-level2, measure1, measure2, .. measure-n ],
				[ ... 											]]
			into a table that can be easily rendered into a crosstab
			The transformed table looks something like :
				[ [ '' 				'' 					row-level1  row-level1  row-level1  row-level1 	... ]
					[ '' 				'' 					row-level2  row-level2  row-level2  row-level2 	... ]
					[ ...                            															]
					[ column-level1 column-level2 fact 		fact 		fact 		fact 		fact	... ]
					[ column-level1 column-level2 fact		fact 		fact		fact  		fact 	... ]
					[ ...  						  															]
				]
			**/
	'use strict';

	const SUMMARY_ID = StringResources.get('summaryCaption');
	const ROW = 'row';
	const COL = 'col';
	const COLUMN = 'column';
	const ORDINAL = 'ordinal';
	const HEAT = 'heat';
	const UNDEFINED = 'undefined';

	class Pivot {

		constructor(options = {}) {
			this.slots = options.slots;
			this.data = options.data;
			this.visModel = options.visModel;
			this.dashboard = options.dashboard;
			this.conditionalFormatting = options.conditionalFormatting;
			this.visFormatSpec = options.visFormatSpec;

			this.rowEdge = {};
			this.columnEdge = {};
			this.valuesMap = {};

			this.colEdgeNestingLevel = 0;
			this.rowEdgeNestingLevel = 0;

			// Index tof the first value (measure)
			this._valuesStartIdx = 0;

			this._measuresInfo = [];
			this._rowInfo = [];
			this._columnInfo = [];
			this._conditionalMeasureInfoNew = [];
			this.indexInfo = {
				row: [],
				col: []
			};
			this.levelInfo = {
				row: {},
				col: {}
			};
			this.summaryValuePromises  = [];

			let slotsAPIs = this.slots.getMappedSlotList();
			if (options && options.data && options.data.getVersion && options.data.getVersion()==='2') {
				this._v2Summaries = true;
			} else {
				this.summaryAPI = this.visModel.ownerWidget.getFeature('summaryFeature');
			}

			const resultDataItemList = QueryResultDataUtils.getResultDataItemList(this.data.getResultItemList());
			let oSlot, aDataItems, dataItem;
			for (let slotIndex = 0; slotIndex < slotsAPIs.length; slotIndex++) {
				oSlot = slotsAPIs[slotIndex];
				aDataItems = oSlot.getDataItemList();

				if (oSlot.getId().indexOf(ROW) !== -1) {
					this._valuesStartIdx++;
					for (let dataItemIndex = 0; dataItemIndex < aDataItems.length; dataItemIndex++) {
						dataItem = aDataItems[dataItemIndex];
						this.rowEdgeNestingLevel++;
						this._addEdgeDataItemInfo(slotIndex, dataItemIndex, ROW);
						this._rowInfo.push({
							id: dataItem.getId(),
							dataType: oSlot.getDefinition().getType(),
							formatSpec: this._getDataItemFormatSpec(dataItem)
						});
					}
				}

				if (oSlot.getId().indexOf(COLUMN) !== -1) {
					this._valuesStartIdx++;
					for (let dataItemIndex = 0; dataItemIndex < aDataItems.length; dataItemIndex++) {
						dataItem = aDataItems[dataItemIndex];
						this.colEdgeNestingLevel++;
						this._addEdgeDataItemInfo(slotIndex, dataItemIndex, COL);
						this._columnInfo.push({
							id: dataItem.getId(),
							dataType: oSlot.getDefinition().getType(),
							formatSpec: this._getDataItemFormatSpec(dataItem)
						});
					}
				}

				if (oSlot.getDefinition().getType() === ORDINAL) {
					for (let dataItemIndex = 0; dataItemIndex < aDataItems.length; dataItemIndex++) {
						dataItem = aDataItems[dataItemIndex];
						let aggregationType = dataItem.getAggregation();
						let info = {
							id: dataItem.getId(),
							label: dataItem.getLabel(),
							aggregationType,
							formatSpec: this._getDataItemFormatSpec(dataItem),
							columnId: dataItem.getColumnId()
						};
						// exclude the conditional measure from other measures
						if (oSlot.getId() === HEAT) {
							this._conditionalMeasureInfo = info;
							if (this.conditionalFormatting) {
								this._conditionalMeasureInfoNew.push(info);
							}
						} else if (this._measureItemHasData(resultDataItemList, dataItem)) {
							this._measuresInfo.push(info);
						}
					}
					// set measure index for the conditionals
					if (this.conditionalFormatting) {
						const condFormats = this.conditionalFormatting.getConditionalFormatting();
						if (condFormats && condFormats.conditionalFormats) {
							this._conditionalMeasureInfoNew.forEach(conditional => {
								const condFormat = condFormats.conditionalFormats.find(cf => cf.heatId === conditional.id);
								if (condFormat) {
									this._measuresInfo.forEach((measureInfo, index) => {
										if (measureInfo.columnId === condFormat.dataItemId) {
											conditional.measureIndex = index;
										}
									});
								}
							});
						}
					}
				}
			}

			this._colRowInfo = this._columnInfo.concat(this._rowInfo);

			this._columnEdgeHasMeasure = (this._measuresInfo.length > 1 || this.colEdgeNestingLevel === 0);
			if (this._columnEdgeHasMeasure) {
				this.colEdgeNestingLevelWithMeasure = this.colEdgeNestingLevel + 1;
			} else {
				this._crosstabMeasure = this._measuresInfo.length ? this._measuresInfo[0].label : null;
			}
		}

		//Add the dataItem index info to cache
		_addEdgeDataItemInfo(slotIndex, nestedIndex, rowOrCol) {
			if (this.data) {
				if (rowOrCol === ROW) {
					this.indexInfo.row.push({
						slotIndex,
						nestedIndex
					});
				} else {
					this.indexInfo.col.push({
						slotIndex,
						nestedIndex
					});
				}
			}
		}

		create() {
			this._hideSummaries = this.visModel.getPropertyValue('hideSummaries');
			this._generateEdgesAndValueMap();

			//Build the pivot table
			const pivotTable = [];
			this._addColumnEdgeCategories(pivotTable);
			this._addRowEdgeCategoriesAndFactCells(pivotTable);
			return pivotTable;
		}

		getNestingLevels() {
			if (this._columnEdgeHasMeasure) {
				return {
					colNestingLevel: this.colEdgeNestingLevel + 1,
					rowNestingLevel: this.rowEdgeNestingLevel
				};
			} else {
				return {
					colNestingLevel: this.colEdgeNestingLevel,
					rowNestingLevel: this.rowEdgeNestingLevel
				};
			}
		}

		getLevelInfo() {
			return this.levelInfo;
		}

		/**
		 * Extract either a cell value or the "tuple part" of an edge value at the specified nesting depth.
		 * eg: for row n containing tuple(2004, Canada) on the rows (measure on columns)
		 * 	getCellValue(n, 0, 0) would return '2004'
		 * 	getCellValue(n, 0, 1) would return 'Canada'
		 *  getCellValue(n, 1) would return the measure value.
		 * @param {number} i - the row
		 * @param {number} c - the column base (it will be the same for all levels of nesting in a tuple)
		 * @param {number} nestedIndex - the nesting level (which tuple part?)
		 */
		getCellValue(i, c, nestedIndex) {
			let originalResult = this.data.getValue(i, c);
			let result = originalResult;
			const isSummary = result && result.type === this.data.VALUE_TYPE.SUMMARY;
			if (Array.isArray(result) && nestedIndex !== undefined) {
				result = result[nestedIndex];
			}

			if (isSummary) {
				//This code is specific for v2 summaries.
				//If this is an 'outersummary', the tuple length will be shorter than the nesting depth.
				//Backfill with the most detailed summary.
				result = result ||
					(originalResult && originalResult.length && originalResult[originalResult.length - 1]);
				if (result) {
					//Tuples (not tuple parts) are marked as summaries but we extract tupleParts in crosstab.
					//If we need to mark this tuplepart as a summary, mark a copy not the original!
					result = JSON.parse(JSON.stringify(result));
					result.label = SUMMARY_ID;
					result.isSummary = true;
				}
			}
			return result;
		}

		/**
		 * This function calculate the levelInfo for rowEdge and colEdge
		 * @param {string} row or col
		 * @param {number} rowIndex or colIndex
		 * @param {number} levelNumber
		 */
		_determineLevelInfo(type, index, levelNumber) {
			if (this.levelInfo[type][index] !== undefined) {
				const {
					rootLevelnumber,
					maxLevelDepth
				} = this.levelInfo[type][index];
				this.levelInfo[type][index] = {
					rootLevelnumber: Math.min(rootLevelnumber, levelNumber),
					maxLevelDepth: Math.max(maxLevelDepth, levelNumber - rootLevelnumber)
				};
			} else {
				this.levelInfo[type][index] = {
					rootLevelnumber: levelNumber,
					maxLevelDepth: 0
				};
			}
		}

		/**
		 * this function process the incoming data and generates the colEdge, rowEdge and valuesMap which is then used to
		 * to create the pivot table
		 */
		_generateEdgesAndValueMap() {
			let rowEdge = {};
			let columnEdge = {};

			let numberOfRows = this.data.getRowCount();
			let numberOfColumns = QueryResultDataUtils.getDataItemCount(this.data.getResultItemList());
			// projected measure ending index
			let heatIndex = this._valuesStartIdx + this._measuresInfo.length;

			for (let i = 0; i < numberOfRows; i++) {
				// get the row edge dimension values
				let isSummaryCell = false;
				let rowDimensions = [];
				for (let cr = 0; cr < numberOfColumns; cr++) {
					// Make sure we have index info for this column
					if (this.indexInfo.row[cr] !== undefined) {
						const cellValue = this.getCellValue(i, this.indexInfo.row[cr].slotIndex, this.indexInfo.row[cr].nestedIndex);
						//Cellvalue can be null for some values in unbalanced hierarchies.  Only contribute dimensions that are populated.
						if (cellValue) {
							isSummaryCell = isSummaryCell || cellValue.isSummary; //This will only be set for v2.
							rowDimensions.push(cellValue);
							// populate the levelInfo for row edge (fixed columns)
							const levelNumber = cellValue && cellValue.ln;
							if (levelNumber !== undefined) {
								this._determineLevelInfo(COL, cr, levelNumber);
							}
						}
					}
				}
				this._addDimensionToEdge(rowEdge, rowDimensions, this.rowEdgeNestingLevel, ROW);

				// get the column edge dimension values
				let columnDimensions = [];
				for (let cc = 0; cc < numberOfColumns; cc++) {
					// Make sure we have index info for this row
					if (this.indexInfo.col[cc] !== undefined) {
						const cellValue = this.getCellValue(i, this.indexInfo.col[cc].slotIndex, this.indexInfo.col[cc].nestedIndex);
						//Cellvalue can be null for some values in unbalanced hierarchies.  Only contribute dimensions that are populated.
						if (cellValue) {
							isSummaryCell = isSummaryCell || cellValue.isSummary; //This will only be set for v2.
							columnDimensions.push(cellValue);
							// populate the levelInfo for column edge (fixed rows)
							const levelNumber = cellValue && cellValue.ln;
							if (levelNumber !== undefined) {
								this._determineLevelInfo(ROW, cc, levelNumber);
							}
						}
					}
				}
				this._addDimensionToEdge(columnEdge, columnDimensions, this.colEdgeNestingLevel, COLUMN, (this._columnEdgeHasMeasure ? this._measuresInfo : undefined));

				// separate the conditional measure from other measure values
				let measureValues = [];
				for (let cm = this._valuesStartIdx; cm < heatIndex; cm++) {
					measureValues.push(this.getCellValue(i, cm));
				}
				if (this.conditionalFormatting) {
					// get the conditional values
					for (let cm = 0; cm < this._conditionalMeasureInfoNew.length; cm++) {
						this._conditionalMeasureInfoNew[cm].condValue =  !isSummaryCell ? this.getCellValue(i, cm + heatIndex).value : undefined;
					}
				}

				let conditionalValue = (this._conditionalMeasureInfo && !isSummaryCell) ? this.getCellValue(i, heatIndex).value : undefined;
				let dimensions;
				if (this._columnEdgeHasMeasure) {
					//for each measure, add its column label as part of the dimensions array to be process as normal
					for (let j = 0; j < this._measuresInfo.length; j++) {
						dimensions = columnDimensions.concat(this._measuresInfo[j].label).concat(rowDimensions);
						if (this.conditionalFormatting) {
							const conditionalMeasureInfo = this._conditionalMeasureInfoNew.find(condMeasure => condMeasure.measureIndex === j);
							conditionalValue = conditionalMeasureInfo ? conditionalMeasureInfo.condValue : undefined;
							this._addValuesToMap(dimensions, measureValues[j] ? measureValues[j].value : measureValues[j], this._measuresInfo[j], conditionalValue, conditionalMeasureInfo);
						} else {
							this._addValuesToMap(dimensions, measureValues[j] ? measureValues[j].value : measureValues[j], this._measuresInfo[j], conditionalValue);
						}
					}
				} else {
					dimensions = columnDimensions.concat(rowDimensions);
					if (this.conditionalFormatting) {
						conditionalValue = this._conditionalMeasureInfoNew[0] ? this._conditionalMeasureInfoNew[0].condValue : undefined;
						this._addValuesToMap(dimensions, measureValues[0] ? measureValues[0].value : measureValues[0], this._measuresInfo[0], conditionalValue, this._conditionalMeasureInfoNew[0]);
					} else {
						this._addValuesToMap(dimensions, measureValues[0] ? measureValues[0].value : measureValues[0], this._measuresInfo[0], conditionalValue);
					}
				}

			}

			this.rowEdge = this._getEdgeWithSortedKeys(rowEdge, this.indexInfo.row, this.rowEdgeNestingLevel);
			this.columnEdge = this._getEdgeWithSortedKeys(columnEdge, this.indexInfo.col, this.colEdgeNestingLevelWithMeasure || this.colEdgeNestingLevel);

		}

		/**
		 * Returns a new object with edge and sortedKeys info.
		 */
		_getEdgeWithSortedKeys(edge, indicesMap, nestingLevel, level, nestedCategories) {

			level = level || 0;
			const sortedKeys = this._getSortedKeys(edge, indicesMap[level], nestedCategories);
			const newEdgeObject = {
				edge,
				sortedKeys
			};
			if (level < nestingLevel - 1) {
				let categories = nestedCategories || [];
				for (let _category in edge) {
					if (edge.hasOwnProperty(_category)) {
						let nestedEdges = edge[_category].nestedEdges;

						if (nestedEdges) {
							let currLevel = (_category === SUMMARY_ID) ? nestingLevel : level + 1;
							categories[level] = _category;
							edge[_category].nestedEdges = this._getEdgeWithSortedKeys(nestedEdges, indicesMap, nestingLevel, currLevel, categories);
							// reset catergory nesting
							if (level === 0) {
								categories = [];
								// pop catergory nesting
							} else if (categories.length) {
								categories = categories.slice(categories.length);
							}
						}
					}
				}
			}
			return newEdgeObject;
		}

		_getSortedKeys(srcObject, indices, nestedCategories) {
			let sortedKeys = [];

			if (typeof indices === UNDEFINED) {
				for ( let key in srcObject ) {
					if ( key !== SUMMARY_ID && srcObject.hasOwnProperty(key)) {
						sortedKeys.push(key);
					}
				}
			} else {
				let resultItem = this.data.getResultItemList()[indices.slotIndex];

				for (let i = 0, tupleCount = resultItem.getRowCount(); i < tupleCount; i++) {
					let tuple = resultItem.getValue(i);

					// need to compare the tuple nesting to nested categories in order to prevent
					// processing the wrong tuple. First, we need to get the values from the tuple, then
					// based on the nested index (level), only use up to that level for the comparison.

					let good = typeof nestedCategories === UNDEFINED;
					if (!good) {
						good = true;
						// PERFORMANCE - do the comparison without creating or copying arrays as was done below...
						// let selectVals = _.pluck(tuple, 'value');
						// good = _.difference(nestedCategories, selectVals.slice(0, indices.nestedIndex)).length === 0;
						for (let k = 0; k < indices.nestedIndex; k++) {
							if (typeof tuple[k] !== UNDEFINED && typeof nestedCategories[k] !== UNDEFINED) {
								if (nestedCategories[k] !== tuple[k].value) {
									good = false;
									break;
								}
							}
						}
					}
					if (good) {
						for (let j = 0; j < tuple.length; j++) {
							let key = tuple[j].value;
							//Only include key in the sortedKeys if it actualy exists in srcObject
							//(i.e. if a row/column doesn't include a given data item member, don't include it in the crosstab)
							if (key && srcObject[key] && sortedKeys.indexOf(key) === -1) {
								sortedKeys.push(key);
								break;
							}
						}
					}
				}
			}

			if (typeof srcObject[SUMMARY_ID] !== UNDEFINED) {
				// add the summary back as the last key
				sortedKeys.push(SUMMARY_ID);
			}
			return sortedKeys;
		}

		_addColumnEdgeCategories(pivotTable) {
			let colEdgeNestingLevel = this.colEdgeNestingLevelWithMeasure || this.colEdgeNestingLevel;
			let i, j;
			for (i = 0; i < colEdgeNestingLevel; i++) {
				let emptyCells = [];
				for (j = 0; j < this.rowEdgeNestingLevel; j++) {
					emptyCells.push({
						type: 'corner',
						value: this._crosstabMeasure || ''
					});
				}

				pivotTable.push(emptyCells);
			}

			const columnEdgeIterator = new CrosstabEdgeIterator(this.columnEdge);
			let nextColumnCategory, lastValue, rowIndex, category;
			while ((nextColumnCategory = columnEdgeIterator.next())) {
				rowIndex = 0;
				category = nextColumnCategory;
				while (category) {
					lastValue = category;
					pivotTable[rowIndex].push(category);
					rowIndex++;
					rowIndex += this._handleUnbalancedCategory(category, pivotTable, rowIndex);
					category = category.subCategory;
				}
				// if an edge is missing a value for a nested row, we add the value of the parent
				for (j = rowIndex; j < colEdgeNestingLevel; j++) {
					pivotTable[j].push(lastValue);
				}
			}

		}

		_addRowEdgeCategoriesAndFactCells(pivotTable) {
			const rowEdgeIterator = new CrosstabEdgeIterator(this.rowEdge);
			let nextRowCategory, pivotTableRow, category, lastValue, index;
			while ((nextRowCategory = rowEdgeIterator.next())) {
				pivotTableRow = [];
				pivotTable.push(pivotTableRow);

				category = nextRowCategory;
				index = 0;
				while (category) {
					lastValue = category;
					pivotTableRow.push(category);
					index += this._handleUnbalancedCategory(category, pivotTableRow);
					category = category.subCategory;
					index++;
				}

				// if an edge is missing a value for a nested row, we add the value of the parent
				for (let i = index; i < this.rowEdgeNestingLevel; i++) {
					if (lastValue) {
						pivotTableRow.push(lastValue);
						nextRowCategory.descendant_or_self.push(lastValue.value.value ? lastValue.value.value : lastValue.value);
					}
				}

				if (!this.conditionalFormatting) {
					this._addFactCellsToPivotTableRow(nextRowCategory, pivotTableRow);
				} else {
					this._addFactCellsToPivotTableRowNewCF(nextRowCategory, pivotTableRow);
				}
			}

		}

		//If this category is an 'unbalanced parent', it will have a span similar to a row or columnspan.
		//Fill the edges of the pivotTable for each of missing descendant up to the span.
		//Note: for the rowEdge, rowIndex is undefined and pivotTable represents a single row.
		_handleUnbalancedCategory(category, pivotTable, rowIndex=undefined) {
			if (!category.isSummary && category.value && category.value.span) {
				const unbalancedCellDepth=category.value.span - 1;
				for (var i=0; i<unbalancedCellDepth; ++i) {
					//Clone the cell, clear the value and mark it as an unbalanced cell.
					const unbalancedCell = JSON.parse(JSON.stringify(category));
					unbalancedCell.isUnbalanced=true;
					delete unbalancedCell.value;
					if (rowIndex!==undefined) {
						//For the column edge, we need to push data 1 cell to the right for span nested "rows" of the column edge
						pivotTable[rowIndex+i].push(unbalancedCell);
					} else {
						//For the row edge, we need to push cells in a single row, span cells to the right.
						pivotTable.push(unbalancedCell);
					}
				}
				return unbalancedCellDepth;
			}
			return 0;
		}

		_addFactCellsToPivotTableRow(rowEdge, pivotTableRow) {

			const columnEdgeIterator = new CrosstabEdgeIterator(this.columnEdge);
			const maxLevel = this.getNestingLevels().colNestingLevel - 1;
			let isOverallSummary, key, tuple, valueObj, rawValue, value, condValue, columnEdge, condProjected;
			if (this._conditionalMeasureInfo && this._measuresInfo) {
				condProjected = this._measuresInfo.find(info => info.label === this._conditionalMeasureInfo.label);
			}
			while ((columnEdge = columnEdgeIterator.next(undefined, maxLevel))) {
				isOverallSummary = rowEdge.isOverallSummary || columnEdge.isOverallSummary;
				key = this.getKeyFromDataCells(columnEdge.descendant_or_self, rowEdge.descendant_or_self);
				tuple = this.getKeyFromDataCells(columnEdge.descendant_or_self_without_measures, rowEdge.descendant_or_self_without_measures);
				valueObj = this.valuesMap[key];
				if (valueObj) {
					rawValue = valueObj.value;
				}
				// We need distinguish the null data point values from DSS for formatting purpose.
				// If missing by default: data should be undefined
				// If explicitly from DSS: data becomes null
				value = valueObj ? rawValue : null;
				condValue = null;
				if (rawValue !== undefined && rawValue !== null) {
					// populate with the conditional measure value
					if (this._conditionalMeasureInfo) {
						// conditional format only applies to the projected conditional measure
						// if the conditional measure is not projected, then format the entire crosstab
						if (valueObj && (this._conditionalMeasureInfo.label === valueObj.label || !condProjected)) {
							condValue = {
								value: valueObj.conditionalValue,
								formatted: Formatter.formatNull(valueObj.conditionalValue, this._conditionalMeasureInfo.formatSpec)
							};
						}
					}
				}
				pivotTableRow.push({
					rowEdge,
					columnEdge,
					value,
					rawValue,
					key,
					tuple,
					condValue,
					isOverallSummary
				});
			}
		}

		_addFactCellsToPivotTableRowNewCF(rowEdge, pivotTableRow) {

			const columnEdgeIterator = new CrosstabEdgeIterator(this.columnEdge);
			const maxLevel = this.getNestingLevels().colNestingLevel - 1;
			let isOverallSummary, key, tuple, valueObj, rawValue, value, condValue, columnEdge;

			while ((columnEdge = columnEdgeIterator.next(undefined, maxLevel))) {
				isOverallSummary = rowEdge.isOverallSummary || columnEdge.isOverallSummary;
				key = this.getKeyFromDataCells(columnEdge.descendant_or_self, rowEdge.descendant_or_self);
				tuple = this.getKeyFromDataCells(columnEdge.descendant_or_self_without_measures, rowEdge.descendant_or_self_without_measures);
				valueObj = this.valuesMap[key];
				if (valueObj) {
					rawValue = valueObj.value;
				}
				// We need distinguish the null data point values from DSS for formatting purpose.
				// If missing by default: data should be undefined
				// If explicitly from DSS: data becomes null
				value = valueObj ? rawValue : null;
				condValue = null;
				if (rawValue !== undefined && rawValue !== null) {
					// populate with the conditional measure value
					if (valueObj) {
						let conditionalMeasure = this._conditionalMeasureInfoNew.find(condMeasure => {
							return condMeasure.label === valueObj.conditionalLabel;
						});
						if(conditionalMeasure) {
							condValue = {
								value: valueObj.conditionalValue,
								formatted: Formatter.formatNull(valueObj.conditionalValue, conditionalMeasure.formatSpec)
							};
						}
					}
				}
				pivotTableRow.push({
					rowEdge,
					columnEdge,
					value,
					rawValue,
					key,
					tuple,
					condValue,
					isOverallSummary
				});
			}
		}

		getKeyFromDataCells(aData, bData) {
			const aKeys = [];
			if (Array.isArray(aData)) {
				for (let i = 0; i < aData.length; i++) {
					aKeys.push(aData[i].value ? aData[i].value : aData[i]);
				}
			}
			if (Array.isArray(bData)) {
				for (let i = 0; i < bData.length; i++) {
					aKeys.push(bData[i].value ? bData[i].value : bData[i]);
				}
			}
			return aKeys.toString();
		}

		_addValuesToMap(dimensions, measureValue, measureInfo, conditionalValue, conditionalValueInfo) {
			if (measureValue === undefined) { // undefined
				return;
			}
			this.valuesMap[this.getKeyFromDataCells(dimensions)] = {
				label: measureInfo ? measureInfo.label : null,
				value: measureValue,
				conditionalLabel: conditionalValueInfo ? conditionalValueInfo.label : null,
				conditionalValue: conditionalValue
			};
		}

		_addDimensionToEdge(edge, dimensions, nestingLevel, type, measuresInfo, currentLevel) {
			currentLevel = currentLevel || 0;
			if (dimensions && dimensions.length > 0) {
				let memberKey = dimensions[0].value;
				if (!edge[memberKey]) {
					edge[memberKey] = {
						level: currentLevel,
						type: type,
						value: dimensions[0]
					};
					if (dimensions[0].isSummary) {
						edge[memberKey].isSummary = dimensions[0].isSummary;
					}
					let formatSpec = null;

					if (type === ROW) {
						formatSpec = this._rowInfo[currentLevel].formatSpec;
					} else if (type === COLUMN) {
						formatSpec = this._columnInfo[currentLevel].formatSpec;
					}
					edge[memberKey].formatSpec = formatSpec;
				}
				let remaining = dimensions.slice(1, dimensions.length);
				if (remaining.length > 0) {
					if (!edge[memberKey].nestedEdges) {
						edge[memberKey].nestedEdges = {};
					}
					this._addDimensionToEdge(edge[memberKey].nestedEdges, remaining, nestingLevel, type, measuresInfo, currentLevel + 1);
				} else if (this._columnEdgeHasMeasure && type === COLUMN) {
					//if we get here, this means we are at the innermost level of the nesting, so add the measures if there are any
					if (!edge[memberKey].nestedEdges) {
						edge[memberKey].nestedEdges = {};
						edge[memberKey].hasMeasureChildren = true;
					}
					this._addMeasureToEdge(edge[memberKey].nestedEdges, measuresInfo, type, currentLevel + 1, false /*bIsSummary*/ );
				}
			} else if (this._columnEdgeHasMeasure && type === COLUMN) {
				//if we get here, it means that there are no dimensions
				this._addMeasureToEdge(edge, measuresInfo, type, currentLevel, false /*bIsSummary*/ );
			}
		}

		_addMeasureToEdge(edge, measuresInfo, type, currentLevel, bIsSummary, bIsOverallSummary) {
			if (measuresInfo) {
				let measure, measureLabel;
				for (let i = 0; i < measuresInfo.length; i++) {
					measure = measuresInfo[i];
					measureLabel = measure.label;
					if (!edge[measureLabel]) {
						edge[measureLabel] = {
							level: currentLevel,
							type: type,
							isMeasure: true
						};
					}
					if (bIsSummary) {
						edge[measureLabel].isSummary = true;
						// make sure the measures in the Summary are not sorted
						edge[measureLabel].noSort = true;
						if (bIsOverallSummary) {
							edge[measureLabel].isOverallSummary = true;
						}

					}
				}
			}
		}

		/**
		 * Not every data item mapped in slot have data return from query result,
		 * in a situation such as suppression where all values of a measure were suppressed.
		 * Verify and return a boolean indicating whether there are data returned for the mapped data item in slot
		 *
		 * @param {array} resultDataItemList list of data items returned from query result
		 * @param {object} dataItemFromSlot mapped slot data item to verify
		 * @return {boolean} true if the dataItem has data returned for turn false
		 */
		_measureItemHasData (resultDataItemList = [], dataItemFromSlot) {
			if (!dataItemFromSlot) {
				throw new Error ('Invalid dataItem provided');
			}
			return !!resultDataItemList.find(item => {
				return item.getId() === dataItemFromSlot.getId();
			});
		}

		_getDataItemFormatSpec (dataItem) {
			let formatSpec;
			if (dataItem) {
				formatSpec = dataItem.getFormat();
				if (this.visFormatSpec) {
					formatSpec = Object.assign({}, formatSpec, this.visFormatSpec);
				}
			}
			return formatSpec;
		}
	}

	return Pivot;
});
